/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ComponentType } from '@angular/cdk/portal';
import { ComponentRef, Renderer2, TemplateRef, ViewContainerRef } from '@angular/core';
import { getScrollbarWidth } from '@gh/core-util';

import { GridApi, GridOptions } from 'ag-grid-community';
import type { ProcessRowParams } from 'ag-grid-community';
import invariant from 'invariant';

import { GRID_EVENT } from '../../grid-public-events';
import { GridFeature, GridFeatureInitParams, GridFeatureReadyParams } from '../feature.interfaces';
import { GridRowInitParams, GridRowOptionsComponent } from './grid-row-options-component';
import { GridTemplateBasedOptionBarComponent } from './grid-template-based-option-bar.component';

export abstract class AbstractGridOptionBarFeature implements GridFeature {
  protected renderer: Renderer2;

  protected viewContainerRef: ViewContainerRef;
  protected nativeElement: HTMLElement;
  protected api: GridApi;

  protected suppressedEvents: string[] = ['click'];

  private viewportEl: HTMLElement;
  private scrollableContainer: HTMLElement;
  private templateRef: TemplateRef<any>;
  private componentClass: ComponentType<any>;
  private initialized = false;

  initGrid(params: GridFeatureInitParams): void {
    this.viewContainerRef = params.viewContainerRef;
    this.nativeElement = params.nativeElement;
  }

  enhanceGridOptions(options: GridOptions): void {
    // options.processRowPostCreate = params => this.process(params);
  }

  onGridReady(params: GridFeatureReadyParams): void {
    this.api = params.api;
    this.initialized = true;
  }

  onDestroy(): void {
  }

  setSuppressedEvents(suppressedEvents: string[]): void {
    this.suppressedEvents = suppressedEvents;
  }

  setTemplateRef(templateRef: TemplateRef<any>): void {
    this.templateRef = templateRef;
  }

  abstract process(params: ProcessRowParams): void;

  protected addDataChangeListener<C extends GridRowOptionsComponent>(componentRef: ComponentRef<C>,
                                                                     params: GridRowInitParams): void {
    params.node.addEventListener('dataChanged', dataChangeListener);

    params.addRenderedRowListener(GRID_EVENT.virtualRowRemoved, () => {
      params.node.removeEventListener('dataChanged', dataChangeListener);
    });

    function dataChangeListener(): void {
      componentRef.instance.refresh({
        node: params.node,
        data: params.node.data,
        context: params.context,
        api: params.api,
        columnApi: params.api,
      });
    }
  }

  protected getBestRightPosition(row: GridRowInitParams): number {
    const viewportEl = this.getViewportElement();
    const { offsetWidth } = viewportEl;
    const scrollContainer = viewportEl.children[0];
    const scrollWidth = scrollContainer.clientWidth  || offsetWidth;
    const scrollLeft = this.getScrollableContainer().scrollLeft;
    
    return (scrollWidth <= offsetWidth ? 0 : scrollWidth - offsetWidth + this.getScrollInitGap()) - scrollLeft + 5;
  }

  protected attachComponent<C extends GridRowOptionsComponent>(row: GridRowInitParams,
                                                               componentRef: ComponentRef<C>): void {
    this.renderer.addClass(componentRef.location.nativeElement, 'grid-row-options');
    this.renderer.setStyle(componentRef.location.nativeElement, 'right', this.getBestRightPosition(row) + 'px');

    this.suppressedEvents.map((eventType) => ({
      type: eventType,
      listener: (event: Event) => event.stopPropagation(),
    })).forEach((event) => {
      componentRef.location.nativeElement.addEventListener(event.type, event.listener);
    });

    componentRef.instance.initRowOptions(row);

    row.eRow.appendChild(componentRef.location.nativeElement);

    this.addDataChangeListener(componentRef, row);
  }

  protected detachComponent(row: GridRowInitParams): void {
    // nothing to do
  }

  protected createComponent<C extends GridRowOptionsComponent>(): ComponentRef<C> {
    if (this.componentClass) {
      return this.viewContainerRef.createComponent(this.componentClass);
    } else if (this.templateRef) {
      const optionBar = this.viewContainerRef.createComponent(GridTemplateBasedOptionBarComponent);
      optionBar.instance.setTemplateRef(this.templateRef);
      return optionBar as any;
    }

    throw new Error('Neither component type nor template ref was defined');
  }

  protected getScrollableContainer(): HTMLElement {
    if (this.scrollableContainer) {
      return this.scrollableContainer;
    }
    this.scrollableContainer  = <HTMLElement>this.nativeElement.querySelector('.ag-center-cols-viewport');
    invariant(this.scrollableContainer, 'Cannot find scrollable element of a grid');
    return this.scrollableContainer;
  }

  protected getViewportElement(): HTMLElement {
    if (this.viewportEl) {
      return this.viewportEl;
    }

    this.viewportEl = <HTMLElement>this.nativeElement.querySelector('.ag-body-horizontal-scroll-viewport');

    invariant(this.viewportEl, 'Cannot find viewport element of a grid');

    return this.viewportEl;
  }

  /**
   *  Horizontal scroll takes place from the left bound of grid (<code>left</code>) to
   *  the right bound of grid (<code>right</code>) minus width of the vertical scrollbar (<code>vscrollbar_width</code>)
   *  On init when grid is not fully build horizontal scroll is build from the
   *  <code>left</code> to <code>right</code> component and does not take into account <code>vscrollbar_width</code>.
   *  This method allows to compensate this difference by adding <code>vscrollbar_width</code> if its is absent on init.
   */
  private getScrollInitGap(): number {
    if (this.initialized) {
      return 0;
    } else {
      return getScrollbarWidth();
    }
  }
}
